import type { CatalogueItem } from './ndis-catalogue';

export interface CatalogueCategoryGroup {
	category: string;
	items: CatalogueItem[];
}

export function isCatalogueItem(value: unknown): value is CatalogueItem {
	if (typeof value !== 'object' || value === null) return false;
	const record = value as Record<string, unknown>;
	return typeof record['supportItemNumber'] === 'string'
		&& typeof record['supportItemName'] === 'string'
		&& typeof record['registrationGroup'] === 'string'
		&& typeof record['supportCategory'] === 'string'
		&& typeof record['unit'] === 'string'
		&& typeof record['quoteRequired'] === 'boolean'
		&& typeof record['nonFaceToFace'] === 'boolean'
		&& typeof record['providerTravel'] === 'boolean'
		&& typeof record['shortNoticeCancellation'] === 'boolean'
		&& typeof record['remotePriceLimit'] === 'string'
		&& typeof record['veryRemotePriceLimit'] === 'string'
		&& typeof record['priceLimit'] === 'string';
}

export function readCatalogueFromStorage(storageKey: string): CatalogueItem[] {
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		if (Array.isArray(parsed)) return parsed.filter(isCatalogueItem);
		if (typeof parsed === 'object' && parsed !== null) {
			const record = parsed as Record<string, unknown>;
			const items = record['items'];
			if (Array.isArray(items)) return items.filter(isCatalogueItem);
		}
		return [];
	} catch {
		return [];
	}
}

export function searchCatalogueItems(items: CatalogueItem[], query: string, limit?: number): CatalogueItem[] {
	const normalizedQuery = query.trim().toLowerCase();
	const matched = normalizedQuery
		? items.filter((item) => `${item.supportItemNumber} ${item.supportItemName} ${item.supportCategory} ${item.registrationGroup}`.toLowerCase().includes(normalizedQuery))
		: items;
	return typeof limit === 'number' ? matched.slice(0, limit) : matched;
}

export function countCatalogueMatches(items: CatalogueItem[], query: string): number {
	return searchCatalogueItems(items, query).length;
}

export function groupCatalogueByCategory(items: CatalogueItem[]): CatalogueCategoryGroup[] {
	const groups = new Map<string, CatalogueItem[]>();
	for (const item of items) {
		const category = item.supportCategory || 'Uncategorised';
		const group = groups.get(category) ?? [];
		group.push(item);
		groups.set(category, group);
	}
	return [...groups.entries()]
		.map(([category, categoryItems]) => ({ category, items: categoryItems }))
		.sort((a, b) => a.category.localeCompare(b.category));
}

export function parseCatalogueRate(value: string): number {
	const parsed = Number(value.replace(/[$,\s]/g, ''));
	return Number.isFinite(parsed) ? parsed : 0;
}
